import { useEffect, useRef, useCallback, useState } from 'react';
import { useAuth } from '../store/authStore';
import { toast } from 'sonner';

const WS_BASE = process.env.REACT_APP_BACKEND_URL
  ?.replace('https://', 'wss://')
  ?.replace('http://', 'ws://');

export const useNotifications = () => {
  const { token } = useAuth();
  const wsRef = useRef(null);
  const [connected, setConnected] = useState(false);
  const [events, setEvents] = useState([]);
  const [onlineUsers, setOnlineUsers] = useState([]);
  const reconnectTimer = useRef(null);

  const connect = useCallback(() => {
    if (!token || !WS_BASE) return;
    if (wsRef.current?.readyState === WebSocket.OPEN) return;

    try {
      const ws = new WebSocket(`${WS_BASE}/api/ws/notifications/${token}`);
      wsRef.current = ws;

      ws.onopen = () => {
        setConnected(true);
      };

      ws.onmessage = (e) => {
        try {
          const data = JSON.parse(e.data);

          // Handle pong silently
          if (data.type === 'pong') return;

          // Handle connection welcome — includes online users list
          if (data.type === 'connected') {
            if (data.online_users) setOnlineUsers(data.online_users);
            return;
          }

          // Online/offline events
          if (data.type === 'team_online' || data.type === 'team_offline') {
            if (data.online_users) setOnlineUsers(data.online_users);
            return;
          }

          // Push to event feed
          setEvents((prev) => [data, ...prev].slice(0, 50));

          // Toast notifications
          if (data.type === 'form_completed') {
            toast.success(`New submission: ${data.form_title}`, {
              description: `${data.total_responses} responses collected`,
            });
          } else if (data.type === 'session_started') {
            toast.info(`New session started: ${data.form_title}`);
          } else if (data.type === 'team_invited') {
            toast.info(`${data.user_name} invited ${data.invited_email} as ${data.role}`);
          } else if (data.type === 'team_removed') {
            toast.info(`${data.user_name} removed ${data.removed_email} from team`);
          }
        } catch {}
      };

      ws.onclose = () => {
        setConnected(false);
        reconnectTimer.current = setTimeout(connect, 5000);
      };

      ws.onerror = () => {
        ws.close();
      };
    } catch {}
  }, [token]);

  useEffect(() => {
    connect();
    const pingInterval = setInterval(() => {
      if (wsRef.current?.readyState === WebSocket.OPEN) {
        wsRef.current.send('ping');
      }
    }, 30000);

    return () => {
      clearInterval(pingInterval);
      clearTimeout(reconnectTimer.current);
      wsRef.current?.close();
    };
  }, [connect]);

  return { connected, events, onlineUsers };
};
